/**
 * A token value is **typed data, never a CSS string.**
 *
 * That is the whole reason this package exists as data rather than as a stylesheet. §9 asks for
 * tokens that generate "CSS custom properties now and a Compose theme later", and a `'12px'` or a
 * `'0 12px 30px rgba(0,0,0,.45)'` in the data would already have decided that the consumer is CSS —
 * Compose wants `12.dp`, an `Elevation`, and a `Color(0xFF0B0D10)`, and it cannot get any of them
 * back out of a string without re-parsing what we just formatted. It is core rule R5 ("numbers and
 * node trees, never formatted strings") applied to the design system.
 *
 * So: a shadow is layers with numbers, an easing curve is four control points, a font stack is a
 * list of family names, and a colour is four channels. `css.ts` is the only place that turns any of
 * it into text.
 */

export interface ShadowLayer {
  readonly xPx: number;
  readonly yPx: number;
  readonly blurPx: number;
  readonly spreadPx: number;
  readonly color: ColorValue;
}

export interface ColorValue {
  readonly kind: 'color';
  /** 0–255. */
  readonly r: number;
  readonly g: number;
  readonly b: number;
  /** 0–1. */
  readonly a: number;
}

export interface PxValue {
  readonly kind: 'px';
  readonly n: number;
}

/** Unitless: a font weight, a line height, a flex ratio. */
export interface NumberValue {
  readonly kind: 'number';
  readonly n: number;
}

export interface MsValue {
  readonly kind: 'ms';
  readonly n: number;
}

export interface FontStackValue {
  readonly kind: 'fontStack';
  readonly families: readonly string[];
}

export interface BezierValue {
  readonly kind: 'bezier';
  readonly points: readonly [number, number, number, number];
}

export interface ShadowValue {
  readonly kind: 'shadow';
  readonly layers: readonly ShadowLayer[];
}

export type TokenValue =
  | PxValue
  | NumberValue
  | MsValue
  | ColorValue
  | FontStackValue
  | BezierValue
  | ShadowValue;

/**
 * Each constructor returns its **own** type rather than the union, so a consumer that needs the number
 * can read it: `layout.previewWidth.n` is what keeps the storyboard tile's CSS width and core's crop
 * arithmetic on the same value. Returning `TokenValue` would force a `kind` check at every such call
 * site and quietly encourage a second copy of the number instead.
 */
export function px(n: number): PxValue {
  return { kind: 'px', n };
}

export function num(n: number): NumberValue {
  return { kind: 'number', n };
}

export function ms(n: number): MsValue {
  return { kind: 'ms', n };
}

export function fontStack(...families: readonly string[]): FontStackValue {
  return { kind: 'fontStack', families };
}

export function bezier(a: number, b: number, c: number, d: number): BezierValue {
  return { kind: 'bezier', points: [a, b, c, d] };
}

export function shadow(...layers: readonly ShadowLayer[]): ShadowValue {
  return { kind: 'shadow', layers };
}

export function rgba(r: number, g: number, b: number, a: number): ColorValue {
  return { kind: 'color', r, g, b, a };
}

const HEX_DIGITS = '0123456789abcdef';

function hexPair(text: string, at: number): number {
  const hi = HEX_DIGITS.indexOf(text[at] ?? '');
  const lo = HEX_DIGITS.indexOf(text[at + 1] ?? '');
  if (hi < 0 || lo < 0) return -1;
  return hi * 16 + lo;
}

/**
 * `#rrggbb` → channels. Throws rather than returning a fallback: a mistyped colour is a mistake in
 * *this* package's own source, at module-evaluation time, and a silent magenta would be a worse
 * outcome than a stack trace pointing at the line.
 *
 * Hand-rolled because `parseInt` on a slice would accept `#0b0d1z` as `0b0d1` (R1 also bans nothing
 * here — this is simply the same "total parser" discipline the API layer uses).
 */
export function hex(text: string): ColorValue {
  const body = text.startsWith('#') ? text.slice(1).toLowerCase() : text.toLowerCase();
  if (body.length !== 6) throw new Error(`token colour must be #rrggbb: ${text}`);

  const r = hexPair(body, 0);
  const g = hexPair(body, 2);
  const b = hexPair(body, 4);
  if (r < 0 || g < 0 || b < 0) throw new Error(`token colour must be #rrggbb: ${text}`);

  return { kind: 'color', r, g, b, a: 1 };
}

/** The same colour at a different alpha — for the hover and scrim washes. */
export function alpha(color: ColorValue, a: number): ColorValue {
  return { kind: 'color', r: color.r, g: color.g, b: color.b, a };
}
